"use strict";

exports.__esModule = true;
exports.UiPreferencesProvider = UiPreferencesProvider;
var _react = _interopRequireWildcard(require("react"));
var _featureFlags = require("../../featureFlags");
var _resolveUiDesign = require("../Theme/UiDesign/resolveUiDesign");
var _EnvContext = require("../Env/EnvContext");
var _RestAPIContext = require("../API/RestAPI/RestAPIContext");
var _UiPreferencesContext = require("./UiPreferencesContext");
var _uiPreferencesApi = require("./uiPreferencesApi");
var _uiPreferences = require("./uiPreferences");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
var STORAGE_PREFIX = "vi.uiPreferences";

// One record per plant per browser. The service copy (when sync is on) is
// the one keyed by user; keying the browser copy by user as well made the
// key change once the profile loaded, so the record read at start was not
// the one written later.
function storageKey(plantName) {
  return STORAGE_PREFIX + "." + (plantName || "plant");
}
function parse(raw) {
  return (0, _uiPreferences.normalizeUiPreferences)(raw ? JSON.parse(raw) : null);
}
function readLocal(key) {
  try {
    var raw = window.localStorage.getItem(key);
    if (raw) {
      return parse(raw);
    }
    // A record an earlier build keyed by user: adopt it, once.
    var prefix = key + ".";
    for (var i = 0; i < window.localStorage.length; i++) {
      var old = window.localStorage.key(i);
      if (old && old.startsWith(prefix)) {
        var adopted = parse(window.localStorage.getItem(old));
        window.localStorage.setItem(key, JSON.stringify(adopted));
        window.localStorage.removeItem(old);
        return adopted;
      }
    }
    return (0, _uiPreferences.normalizeUiPreferences)(null);
  } catch (e) {
    return (0, _uiPreferences.normalizeUiPreferences)(null);
  }
}
var everyScreen = function everyScreen() {
  return Object.keys(_resolveUiDesign.UI_SCREENS).reduce(function (all, screen) {
    all[screen] = true;
    return all;
  }, {});
};
function writeLocal(key, preferences) {
  try {
    window.localStorage.setItem(key, JSON.stringify(preferences));
  } catch (e) {
    // Private mode or blocked storage: the choice lasts for this session.
  }
}
function clearLocal(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // see writeLocal
  }
}

/**
 * Holds the interface preferences: the single record that decides what
 * renders, what the navbar switch shows and what the Interface dialog shows.
 * The browser copy (per plant) is always kept and changes only through the
 * switch, the dialog, or a ?ui= link where the environment allows one; with
 * ENABLE_UI_PREFERENCES_SYNC the copy in svc-licensing is loaded on mount
 * (it wins) and every change is sent there too. The whole layer is on only
 * where the environment sets ENABLE_UI_PREFERENCES: elsewhere `enabled` is
 * false, the effective flags are the environment's own, nothing is synced
 * and ?ui= is ignored, so the switch and the dialog stay hidden. Mount it
 * above the UiDesignProviders.
 */
function UiPreferencesProvider(_ref) {
  var children = _ref.children;
  var env = (0, _react.useContext)(_EnvContext.EnvContext) || {};
  var api = (0, _react.useContext)(_RestAPIContext.RestAPIContext);
  var featureFlags = env.featureFlags;

  // Whether this deployment offers the choice at all. Without the flag the
  // record is kept but never applied, so a later enablement finds it again.
  var enabled = (0, _uiPreferences.uiPreferencesEnabled)(featureFlags);
  var syncEnabled = enabled && Boolean(api) && Array.isArray(featureFlags) && featureFlags.includes(_featureFlags.ENABLE_UI_PREFERENCES_SYNC);
  var overrideAllowed = enabled && Array.isArray(featureFlags) && featureFlags.includes(_featureFlags.ENABLE_MODERN_UI_OVERRIDE);
  var key = storageKey(api && api.plantName);
  var _useState = (0, _react.useState)(function () {
      return readLocal(key);
    }),
    preferences = _useState[0],
    setPreferences = _useState[1];
  var _useState2 = (0, _react.useState)(syncEnabled),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var current = (0, _react.useRef)(preferences);
  current.current = preferences;
  (0, _react.useEffect)(function () {
    setPreferences(readLocal(key));
  }, [key]);
  (0, _react.useEffect)(function () {
    if (!syncEnabled) {
      setLoading(false);
      return undefined;
    }
    var cancelled = false;
    setLoading(true);
    (0, _uiPreferencesApi.fetchUiPreferences)(api).then(function (remote) {
      if (!cancelled) {
        setPreferences(remote);
        writeLocal(key, remote);
      }
    })["catch"](function () {
      // The browser copy stands until the service answers.
    }).then(function () {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return function () {
      cancelled = true;
    };
  }, [syncEnabled, api, key]);

  // Resolves once the browser copy is written and, with sync on, the service
  // has answered (or failed: the browser copy stands either way), so a caller
  // can reload the page right after.
  var commit = (0, _react.useCallback)(function (next) {
    current.current = next;
    setPreferences(next);
    writeLocal(key, next);
    if (!syncEnabled) {
      return Promise.resolve(next);
    }
    return (0, _uiPreferencesApi.storeUiPreferences)(api, next)["catch"](function () {
      return next;
    }).then(function () {
      return next;
    });
  }, [api, key, syncEnabled]);
  var update = (0, _react.useCallback)(function (patch) {
    var before = current.current;
    return commit((0, _uiPreferences.normalizeUiPreferences)(Object.assign({}, before, patch, {
      screens: Object.assign({}, before.screens, patch && patch.screens || {})
    })));
  }, [commit]);
  var reset = (0, _react.useCallback)(function () {
    clearLocal(key);
    return commit((0, _uiPreferences.normalizeUiPreferences)(_uiPreferences.EMPTY_UI_PREFERENCES));
  }, [commit, key]);

  // Replace the whole record (the dialog saves its draft this way).
  var save = (0, _react.useCallback)(function (next) {
    return commit((0, _uiPreferences.normalizeUiPreferences)(next));
  }, [commit]);

  // A ?ui= link is a request to change the record, not a parallel state:
  // apply it once, drop it from the URL, and forget any pin an earlier
  // build stored under its own key so it can never disagree with the record.
  (0, _react.useEffect)(function () {
    (0, _resolveUiDesign.clearUiOverride)();
    if (!overrideAllowed) {
      return;
    }
    var requested = (0, _resolveUiDesign.readUiOverrideParam)();
    if (!requested) {
      return;
    }
    (0, _resolveUiDesign.removeUiOverrideFromUrl)();
    if (requested === _resolveUiDesign.UI_RESET) {
      reset();
    } else if (requested === _resolveUiDesign.UI_MODERN) {
      save({
        modern: true,
        screens: everyScreen()
      });
    } else if (requested === _resolveUiDesign.UI_LEGACY) {
      save({
        modern: false,
        screens: current.current.screens
      });
    }
    // Once per mount: the URL is read and cleaned here, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var value = (0, _react.useMemo)(function () {
    return {
      preferences: preferences,
      environment: (0, _uiPreferences.describeEnvironmentUi)(featureFlags),
      effectiveFlags: (0, _uiPreferences.applyUiPreferences)(featureFlags, preferences),
      enabled: enabled,
      loading: loading,
      syncEnabled: syncEnabled,
      update: update,
      reset: reset,
      save: save
    };
  }, [preferences, featureFlags, enabled, loading, syncEnabled, update, reset, save]);
  return /*#__PURE__*/_react["default"].createElement(_UiPreferencesContext.UiPreferencesContext.Provider, {
    value: value
  }, children);
}